/**
 * Formats a currency value in euro cents, excluding the currency symbol.
 *
 * See also ADR _Currency values [2023-03-09]_.
 */
export function formatCurrency(value: number): string {
  const integerDigits = Math.trunc(value / 100);
  const fractionalDigits = Math.trunc(value) % 100;
  const correctFractionalDigits =
    fractionalDigits < 10 ? `0${fractionalDigits}` : fractionalDigits;
  return `${integerDigits},${correctFractionalDigits}`;
}

/**
 * Parses a string as a currency value and returns the value in euro cents.
 *
 * `value` must match the pattern `\d+,\d{2}` – i.e. contain at least one
 * integer digit, an optional comma separator followed by up to
 * two fractional digits, no currency symbol.
 * Returns `NaN` on invalid inputs.
 *
 * See also ADR _Currency values [2023-03-09]_.
 */
export function parseCurrency(value: string): number {
  const match = value.match(currencyPattern);
  if (!match) {
    return Number.NaN;
  }

  const integerPart = Number.parseInt(match[1]);
  let fractionalPart = match[2] || '0';

  if (fractionalPart.length === 1) {
    fractionalPart += '0';
  }

  return integerPart * 100 + Number.parseInt(fractionalPart);
}

export const currencyPattern = /^(\d+),?(\d{0,2})$/;

const numFormatter = new Intl.NumberFormat('de-DE', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatNumberIntl = (value: number): string =>
  numFormatter.format(value);
